/* eslint-disable no-prototype-builtins */
import { columnCOnfig } from './components/accounting/models/payment-column-config';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuthService } from './auth/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { CommonService } from './helpers/common.service';
import { MatDialog } from '@angular/material/dialog';
import { ISideNavItems } from './models/side-menu';
import { filter, Subscription, tap } from 'rxjs';
import {
  contactSearchOptions,
  Filter,
  invoiceSearchOptions,
  stripeSearchOptions,
} from './common/search/search.model';
import {
  contactResultColumn,
  paymentResultColumn,
  invoiceResultColumn,
} from './common/search/filter-grid/filter-grid.model';
import { NavigationService } from './navigation.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountingService } from './components/accounting/accounting.service';

interface menu {
  name: string;
  icon: string;
  submenu: submenu[];
}

interface submenu {
  name: string;
  path: string;
  icon: string;
}

interface KeyValuePair {
  key: string;
  value: string; // Use union type if values can be numbers
}

interface FilterEvent {
  key?: string;
  value?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  token: string | null = null;
  user: string | null = null;
  showToolbar = false;

  fillerNav = ISideNavItems;
  currentNavName: string = '';
  matchedNav: any;

  currentRoute: string | undefined;
  parentNav: menu = {
    name: '',
    icon: '',
    submenu: [],
  };

  filterValue: string = '';
  filterField: string = '';
  filteredData: KeyValuePair[][] = [];
  pageLevelFilters: Filter[] = [];
  filterObject: any;
  enterKeyPressed: boolean = false;
  searchPage: string = '';
  columnConfig: any[] = [];

  currentPageSubscription: Subscription | null = null;
  page: string = '';
  panel: boolean = false;
  configMap = new Map<string, any>();
  valuetoFilter: string = '';

  constructor(
    private authService: AuthService,
    private router: Router,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private _accountService: AccountingService,
  ) {
    // Initialize and populate the Map in the constructor
    this.configMap = new Map<string, any>();
    this.configMap.set('payments', {
      search: stripeSearchOptions,
      page: 'stripeTransactions',
      column: paymentResultColumn,
    });
    this.configMap.set('account', {
      search: contactSearchOptions,
      page: 'contacts',
      column: contactResultColumn,
    });
    this.configMap.set('invoice', {
      search: invoiceSearchOptions,
      page: 'invoices',
      column: invoiceResultColumn,
    });
  }

  ngOnInit() {
    this.token = sessionStorage.getItem('authToken');
    this.user = sessionStorage.getItem('name');

    this.authService.authState$.subscribe((isAuthenticated) => {
      this.showToolbar = isAuthenticated;
    });

    this.currentPageSubscription =
      this.navigationService.currentPage$.subscribe((pageName) => {
        this.page = pageName;
        this.filterValue = '';
        this.filterField = '';
        this.enterKeyPressed = false;
        // Now you can use this.page in your switch case
      });

    this.getCurrentActivePage();
  }

  getCurrentActivePage() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        // Try to find the matched nav in main items or their submenus
        // 2. Get the current route name from the event
        this.currentRoute = event.urlAfterRedirects.split('/').pop();
        console.log(this.currentRoute);
        localStorage.setItem('currentNavName', this.currentNavName);
        this.matchedNav =
          this.fillerNav.find(
            (nav) =>
              nav.name.toLowerCase() === this.currentRoute?.toLowerCase(),
          ) ||
          this.fillerNav
            .flatMap((nav) => nav.submenu || [])
            .find(
              (subNav) =>
                subNav.name.toLowerCase() === this.currentRoute?.toLowerCase(),
            ) ||
          '';
        if (this.matchedNav && this.matchedNav.name) {
          this.navigationService.setCurrentPage(this.matchedNav.name);
          const pageConfig = this.configMap.get(
            this.matchedNav.name.toLowerCase(),
          );
          if (pageConfig) {
            this.pageLevelFilters = pageConfig.search;
            this.searchPage = pageConfig.page;
            this.columnConfig = pageConfig.column;
          } else {
            // Default configuration
            this.pageLevelFilters = invoiceSearchOptions;
            this.searchPage = 'accounts';
            this.columnConfig = invoiceResultColumn;
          }
        }
        // Append the matched name to fillerNav if found and not already present
        // Find the parent navigation item for the matchedNav (if matchedNav is a submenu)
        const navItem = this.fillerNav.find(
          (nav) =>
            nav.name === this.matchedNav.name ||
            (nav.submenu &&
              nav.submenu.some(
                (subNav) => subNav.name === this.matchedNav.name,
              )),
        );
        if (navItem) this.parentNav = navItem;
        this.currentNavName = this.matchedNav ? this.matchedNav.name : '';
        localStorage.setItem('parentNav', this.parentNav.name);
        localStorage.setItem('currentNavName', this.currentNavName);
      });
  }

  onEnterKeyPressed(event: boolean) {
    this.enterKeyPressed = event;
    this.search();
    this.filteredData = [];
  }

  onSearch(event: string) {
    this.filterValue = event;
    if (event.length === 0) {
      this.filteredData = [];
      this.filterField = '';
      return; // Stop execution if the search term is empty
    }
    // No need for a separate if statement, can be combined
    if (event.length >= 2) {
      this.filterField = '';
      this.filterObject = '';
      this.search();
    }
  }

  searchTerm($event: string) {
    this.valuetoFilter = $event;
  }

  onFilterSelected(event: FilterEvent | string) {
    // Reset fields to ensure a clean state before processing
    this.filterField = '';
    this.filterValue = '';
    this.filterObject = event;

    // Use a more robust check for different event types
    if (typeof event === 'string' && event.includes(':')) {
      const parts = event.split(':');
      this.filterField = parts[0];
      this.filterValue = parts[1];
    } else if (typeof event === 'object' && event !== null) {
      if (event.value && event.value.includes(':')) {
        const parts = event.value.split(':');
        this.filterField = parts[0];
        this.filterValue = parts[1];
      } else if (event.key) {
        this.filterField = event.key;
      } else {
        if (this.valuetoFilter.includes(':')) {
          const parts = this.valuetoFilter.split(':');
          this.filterField = parts[0];
          this.filterValue = parts[1];
        } else this.filterValue = this.valuetoFilter;
        this.filterObject = event;
      }
    }

    // Always call search() at the end after the filter fields are set
    this.search();
  }

  search() {
    const searchParams = {
      filterField: this.filterField ? this.filterField : '',
      filterValue: this.filterValue,
      pageSize: 50,
      pageOffset: 0,
    };
    sessionStorage.setItem('searchParams', JSON.stringify(searchParams));
    this.authService.search(searchParams, this.searchPage).subscribe({
      next: (response: any) => {
        if (response.value.searchResults.length == 0) return;
        response.value.searchResults.forEach((val: any) => {
          val.isClient = val.isClient ? 'Client' : 'Handyman';
        });
        let filteredResponse = [];
        if (this.filterObject.id) {
          this.navigateToDetailsPage(this.filterObject);
        } else if (this.filterObject) {
          filteredResponse =
            response.value.searchResults.length >= 1
              ? this.filterSelectedField(response.value.searchResults)
              : response.value.searchResults;

          this.filteredData =
            filteredResponse.length >= 1 ? filteredResponse : [];

          if (this.enterKeyPressed) {
            this.router.navigate(['/klusup/searchresults'], {
              state: { data: response.value },
            });
            this.enterKeyPressed = false;
          }
        } else {
          this.filteredData = this.getMatchedObjects(
            response.value.searchResults,
            this.filterValue,
            this.columnConfig,
          );
          if (
            this.filteredData.length > 0 &&
            (this.filterField == undefined || this.filterField == '') &&
            this.enterKeyPressed
          ) {
            this.router.navigate(['/klusup/searchresults'], {
              state: { data: response.value },
            });
            this.enterKeyPressed = false;
            this.panel = false;
            // this.filteredData = [];
            this.cdr.detectChanges();
          }
        }
      },
      error: (error) => {
        this._snackBar.open(
          `Error while fetching search data: ${error}`,
          'Close',
          {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          },
        );
      },
    });
  }

  navigateToDetailsPage(dataObj: any) {
    const navMap = {
      Account: {
        serviceMethod: (id: number) => this._accountService.getContactsById(id),
        routePrefix: '/klusup/accounting/account/account-details/',
      },
      Invoice: {
        serviceMethod: (id: number) => this._accountService.getInvoiceById(id),
        routePrefix: '/klusup/accounting/invoice/invoice-details/',
      },
      Payments: {
        serviceMethod: (id: number) => this._accountService.getInvoiceById(id),
        routePrefix: '/klusup/accounting/payments/stripe/',
      },
    };

    const nav = navMap[this.currentNavName as keyof typeof navMap];

    if (nav) {
      nav.serviceMethod(dataObj.id).subscribe({
        next: (res: any) => {
          if (!res.isError) {
            // 'accountData' is not used, so it can be removed
          }
          this.router.navigate([nav.routePrefix, res.value.id], {
            state: { data: res.value },
          });
        },
      });
    } else {
      this._snackBar.open('Navigates to selected record details.', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    }
  }

  filterSelectedField(response: any[]) {
    const lowerCaseFilterKey = this.filterField?.toLowerCase(); // ||this.filterObject?.key?.toLowerCase() ;
    const value = String(
      this.filterValue, //|| this.filterObject?.value ||,
    ).toLowerCase();

    return response.filter((item: any) => {
      // Check if the item has the specified key and if its value includes the filter value
      // Use `toLowerCase()` on the key from the item for a case-insensitive match
      if (
        Object.keys(item).some((key) => key.toLowerCase() == lowerCaseFilterKey)
      ) {
        // Find the correct key on the item object to access the value
        const actualKey = Object.keys(item).find(
          (key) => key.toLowerCase() == lowerCaseFilterKey,
        );
        if (actualKey) {
          const itemValue = String(item[actualKey]).toLowerCase();
          // Use 'includes' for a partial match anywhere in the string
          return itemValue.includes(value);
        }
      }
      return false; // No match found
    });
  }

  getMatchedObjects(
    dataArray: any[],
    searchTerm: string,
    columnsConfig: any[],
  ): any[] {
    if (!dataArray || !searchTerm) {
      return [];
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const searchableKeys = columnsConfig.map((col) => col.key);

    let filteredData = dataArray;

    // Check for a key-value search (e.g., "profileId:109")
    if (searchTerm.includes(':')) {
      const parts = searchTerm.split(':');
      const filterKey = parts[0].toLowerCase();
      const filterValue = parts[1] ? parts[1].toLowerCase() : '';

      // Filter by a specific key and value
      filteredData = dataArray.filter((item) => {
        // Check if the item has the specified key and if its value includes the filter value
        if (item.hasOwnProperty(filterKey)) {
          const itemValue = String(item[filterKey]).toLowerCase();
          return itemValue.includes(filterValue);
        }
        return false;
      });
    } else {
      // Perform a general search across all searchable keys
      filteredData = dataArray.filter((item) => {
        return searchableKeys.some((key) => {
          const value = item[key];
          if (value !== undefined && value !== null) {
            const stringValue = String(value).toLowerCase();
            return stringValue.includes(lowerCaseSearchTerm);
          }
          return false;
        });
      });
    }

    // Now, map the filtered results to create new objects with only the required columns
    const columnKeys = columnsConfig.map((col) => col.key);
    return filteredData.map((item) => {
      const newObject: any = {};
      columnKeys.forEach((key) => {
        if (item.hasOwnProperty(key)) {
          newObject[key] = item[key];
        }
      });
      return newObject;
    });
  }

  logout() {
    this.authService.logout();
    this.token = null;
    sessionStorage.clear();
    this.authService.notifyAuthState(false); // Notify authentication state change
    this.router.navigate(['/login']);
  }

  viewProfile() {
    this.router.navigate(['/klusup/dashboard']);
  }
}
